import React, { CSSProperties } from "react";
import { Canvas } from "@react-three/fiber";
import * as THREE from "three";
import { OrbitControls } from "@react-three/drei";
import Model from "~/Model";
import OutputText from "~/voice_utils/TextOutput";
import RubyText from "~/components/utils/RubyText";
import ThoughtBubble from "~/components/animation/ThoughtBubble";
import FaceDetection from "~/components/FaceDetection";
import Indicator from "~/components/Indicator";

interface ModelViewLayout1Props {
  visible: boolean;
  handleModelViewed: () => void;
  name: string;
  characterIndex: number;
  maxIndex: number;
  isSoundOn: boolean;
  toggleMute: () => void;
  isFaceDetection: boolean;
  setPrevCharacter: () => void | Promise<void>;
  setNextCharacter: () => void | Promise<void>;
  changeButtonsDisabled: boolean;
}

/**
 * 3Dモデルと関連UIを表示するコンポーネント
 */
// レンダラー設定やカメラ・ライト設定（UI 固定値）はローカルに保持
const canvasStyle: CSSProperties = {
  borderRadius: "10px",
  display: "block",
  height: "60% !important",
};

const cameraSettings = {
  fov: 22.5,
  near: 0.1,
  far: 100,
  position: { x: 0, y: -0.05, z: 2.0 },
};

const lightSettings = {
  directional: {
    position: { x: 0, y: 10, z: 10 },
    color: 0xffffff,
    intensity: 3.0,
  },
  ambient: { color: 0xffffff, intensity: 0.5 },
};

const glConfig = {
  antialias: true,
  alpha: true,
  outputColorSpace: THREE.SRGBColorSpace,
  toneMapping: THREE.LinearToneMapping,
  toneMappingExposure: 1.0,
};

const ModelViewLayout1: React.FC<ModelViewLayout1Props> = ({
  visible,
  handleModelViewed,
  name,
  characterIndex,
  maxIndex,
  isSoundOn,
  toggleMute,
  isFaceDetection,
  setPrevCharacter,
  setNextCharacter,
  changeButtonsDisabled,
}) => {
  return (
    <div className="character-area character-area-layout-1">
      <OutputText />
      <div className="volume-btn">
        <button
          className={`btn btn-circle ${isSoundOn ? "sound-on" : "sound-off"}`}
          onClick={toggleMute}
        >
          {isSoundOn ? (
            <span className="material-symbols-rounded sound-on-btn">volume_up</span>
          ) : (
            <span className="material-symbols-rounded sound-off-btn">volume_off</span>
          )}
        </button>
      </div>

      <div className="character-view-layout-1">
        <div className="chatpage-canvas">
          <Canvas
            className={`main-canvas ${visible ? "visible" : "hide"}`}
            // カメラ設定の使用箇所
            camera={{
              fov: cameraSettings.fov,
              near: cameraSettings.near,
              far: cameraSettings.far,
              position: [
                cameraSettings.position.x,
                cameraSettings.position.y,
                cameraSettings.position.z,
              ],
            }}
            flat
            style={canvasStyle}
            gl={glConfig}
          >
            {/* ライト設定の使用箇所 */}
            {/* eslint-disable react/no-unknown-property */}
            <directionalLight
              position-x={lightSettings.directional.position.x}
              position-y={lightSettings.directional.position.y}
              position-z={lightSettings.directional.position.z}
              color={lightSettings.directional.color}
              intensity={lightSettings.directional.intensity}
            />
            <OrbitControls enableZoom={false} enableRotate={false} enablePan={false} />
            <ambientLight
              color={lightSettings.ambient.color}
              intensity={lightSettings.ambient.intensity}
            />
            <Model onViewed={handleModelViewed} />
          </Canvas>
          <ThoughtBubble />
        </div>
        <div className="character-name">
          <RubyText text={name} />
        </div>
      </div>

      {/*効率化のためisFaceDetectionがOFFなら切る*/}
      {isFaceDetection && <FaceDetection />}
      <div className="character-button">
        <div className="change-btn">
          <button
            className="btn btn-circle"
            onMouseUp={() => void setPrevCharacter()}
            disabled={changeButtonsDisabled}
          >
            <span className="material-symbols-rounded">keyboard_arrow_left</span>
          </button>
          <div className="indicator horizontal-only">
            <Indicator characterIndex={characterIndex} maxIndex={maxIndex} />
          </div>
          <button
            className="btn btn-circle"
            onMouseUp={() => void setNextCharacter()}
            disabled={changeButtonsDisabled}
          >
            <span className="material-symbols-rounded">keyboard_arrow_right</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModelViewLayout1;
